import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import Toast from "@/components/base/Toast";
import { getFilmById, getRelatedFilms } from "@/lib/catalog";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/hooks/useToast";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { id } = useParams();
  const film = getFilmById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { message, showToast } = useToast();
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  usePageMeta(
    film
      ? `${film.title} - 日韩伦理电影剧情解析与介绍`
      : "作品未收录 - 日韩伦理片",
    film
      ? `${film.title}（${film.titleEn}，${film.year}，${film.region}${film.category}）由${film.director}执导，${film.cast.join("、")}主演。含剧情简介、深度解析与同类作品推荐。`
      : "该作品条目暂未收录，可返回分类页浏览其他日韩伦理影视作品。",
  );

  if (!film) {
    return (
      <div className="mx-auto max-w-shell px-4 py-16 md:px-8">
        <Breadcrumbs items={[{ label: "作品" }]} />
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这个作品条目"
          description="链接可能已经失效，或者该作品尚未收录。你可以回到分类页，或使用站内搜索按片名、导演、主演查找。"
        >
          <Link
            to="/category/%E7%94%B5%E5%BD%B1"
            className="flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-800 px-4 py-2 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            浏览电影分类
          </Link>
          <Link
            to="/search"
            className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-[12px] text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            前往搜索
          </Link>
        </EmptyState>
      </div>
    );
  }

  const favorite = isFavorite(film.id);
  const related = getRelatedFilms(film, 4);

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
      showToast("链接已复制到剪贴板（演示分享）");
    } catch {
      setCopyState("failed");
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    const next = toggleFavorite(film.id);
    showToast(next ? `已收藏《${film.title}》，仅保存在本机` : `已取消收藏《${film.title}》`);
  };

  return (
    <div className="pb-4">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-shell px-4 py-5 md:px-8 md:py-6">
          <Breadcrumbs
            items={[
              { label: film.category, to: `/category/${encodeURIComponent(film.category)}` },
              { label: film.title },
            ]}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${encodeURIComponent(film.category)}`}
              className="cursor-pointer rounded-full bg-primary-50 px-2.5 py-1 font-label text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-800 transition-colors hover:bg-primary-100"
            >
              {film.category}
            </Link>
            <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-[10px] text-secondary-900">
              {film.region}
            </span>
            <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-[10px] text-secondary-900">
              {film.year}
            </span>
            {film.editorPick && (
              <span className="rounded-full bg-accent-100 px-2.5 py-1 font-label text-[10px] font-semibold text-accent-900">
                编辑推荐
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-shell px-4 pt-8 md:px-8 md:pt-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr] lg:gap-12">
          <aside className="w-full">
            <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-secondary-100">
              <img
                src={film.poster}
                alt={`${film.title} ${film.titleEn} 作品封面`}
                title={`${film.title} - ${film.genres.join(" ")}`}
                className="aspect-[2/3] w-full object-cover object-top"
              />
              <span className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-background-50/95 px-2 py-1 font-label text-[12px] font-bold text-primary-900">
                <i className="ri-star-fill text-accent-500" />
                {film.score.toFixed(1)}
              </span>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-[13px] font-medium transition-colors ${
                  favorite
                    ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                    : "bg-primary-800 text-background-50 hover:bg-primary-700"
                }`}
              >
                <i className={`${favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} text-base`} />
                {favorite ? "已收藏 · 点击取消" : "收藏这部作品"}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-[13px] text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className={`${copyState === "copied" ? "ri-checkbox-circle-line" : "ri-link"} text-base`} />
                复制页面链接
              </button>
            </div>

            <dl className="mt-5 divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 text-[12px]">
              {[
                { label: "导演", value: film.director },
                { label: "主演", value: film.cast.join(" / ") },
                { label: "年份", value: String(film.year) },
                { label: "地区", value: film.region },
                { label: "类型", value: film.genres.join(" · ") },
                { label: "时长", value: film.duration },
                { label: "上线", value: film.addedAt },
              ].map((row) => (
                <div key={row.label} className="flex gap-3 px-4 py-2.5">
                  <dt className="w-10 shrink-0 font-label text-foreground-400">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          </aside>

          <div className="min-w-0">
            <h1 className="font-heading text-[30px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[42px]">
              {film.title}
            </h1>
            <p className="mt-1.5 font-label text-[12px] uppercase tracking-[0.18em] text-secondary-500">
              {film.titleEn} · {film.year} · {film.duration}
            </p>

            <div className="mt-5 border-l-2 border-accent-500 bg-accent-50/60 px-4 py-3">
              <p className="text-[14px] leading-relaxed text-foreground-800 md:text-[15px]">{film.tagline}</p>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2 text-[12px] transition-colors ${
                  favorite
                    ? "border-accent-400 bg-accent-100 text-accent-900"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} />
                {favorite ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-[12px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-share-forward-line" />
                分享链接（演示）
              </button>
              {["ri-weibo-line", "ri-twitter-x-line", "ri-mail-line"].map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={handleCopy}
                  aria-label="复制链接后前往社交平台分享"
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={icon} />
                </button>
              ))}
              <span className="font-label text-[10px] text-foreground-400">
                仅复制链接，不自动发布
              </span>
            </div>

            <section className="mt-9">
              <h2 className="flex items-center gap-2 font-heading text-[19px] font-bold text-foreground-950 md:text-[21px]">
                <span className="h-[3px] w-4 bg-accent-500" />
                剧情简介
              </h2>
              <p className="mt-3 text-[14px] leading-[1.95] text-foreground-700">{film.summary}</p>
            </section>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-[19px] font-bold text-foreground-950 md:text-[21px]">
                <span className="h-[3px] w-4 bg-accent-500" />
                深度解析
              </h2>
              <div className="mt-3 space-y-4">
                {film.analysis.map((paragraph, index) => (
                  <div key={index} className="flex gap-3">
                    <span className="mt-1 shrink-0 font-label text-[11px] font-semibold text-accent-700">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[14px] leading-[1.95] text-foreground-700">{paragraph}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-8 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
              <h2 className="font-heading text-[15px] font-bold text-foreground-950">所属分类与标签</h2>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${encodeURIComponent(film.category)}`}
                  className="flex cursor-pointer items-center gap-1 rounded-full bg-primary-800 px-3 py-1.5 text-[11px] text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-folder-line text-xs" />
                  {film.category}分类
                </Link>
                {film.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="cursor-pointer rounded-full bg-accent-100 px-3 py-1.5 text-[11px] text-accent-900 transition-colors hover:bg-accent-200"
                  >
                    {genre}
                  </Link>
                ))}
                {film.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-[11px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>

        <section className="mt-14 border-t border-background-200 pt-10">
          <SectionHeading
            kicker="Related Titles"
            title="相关推荐"
            description="按题材、地区与主题标签匹配，方便顺着一个线索继续看下去。"
            actionLabel={`更多${film.category}`}
            actionTo={`/category/${encodeURIComponent(film.category)}`}
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
            {related.map((item) => (
              <FilmCard key={item.id} film={item} showSummary={false} />
            ))}
          </div>
        </section>
      </div>

      <Toast message={message} />
    </div>
  );
}